import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { RoleKey, UserDto } from '@tww/shared';
import { ROLE_KEYS, ROLE_NAMES, fmtDateTime } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, Note, Panel, Skeleton } from '../../ui/basics';
import { ConfirmDialog } from '../../ui/Modal';
import { useToast } from '../../ui/Toast';
import { Dropdown } from '../../ui/Dropdown';

type Pending = { kind: 'role'; user: UserDto; role: RoleKey | '' } | { kind: 'deactivate'; user: UserDto };

/** Nutzer: jede Person bekommt genau eine Rolle; was die Rolle darf, steht unter „Rollen und Rechte“. */
export function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useAuth();
  const [pending, setPending] = useState<Pending | null>(null);
  const q = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: UserDto[] }>('/users').then((r) => r.items) });
  const setRole = useMutation({
    mutationFn: ({ user, role }: { user: UserDto; role: RoleKey | '' }) => api.put(`/users/${user.id}/roles`, { roles: role ? [role] : [] }),
    onSuccess: (_r, { user, role }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setPending(null);
      toast(role ? `${user.display_name} hat jetzt die Rolle „${ROLE_NAMES[role]}“` : `${user.display_name} hat keine Rolle mehr`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const patch = useMutation({
    mutationFn: ({ user, ...b }: { user: UserDto; initials?: string | null; is_active?: boolean; daily_digest?: boolean }) => api.patch(`/users/${user.id}`, b),
    onSuccess: (_r, { user, ...b }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setPending(null);
      toast(b.is_active === false ? `${user.display_name} deaktiviert` : b.is_active ? `${user.display_name} aktiviert` : 'Gespeichert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const noRole = q.data!.filter((u) => u.roles.length === 0 && u.is_active).length;
  const self = (u: UserDto) => me?.user.id === u.id;
  return (
    <>
      <Note kind="info">
        Neue Nutzer erscheinen hier nach der ersten Anmeldung mit Microsoft 365. Was eine Rolle darf, legst du unter{' '}
        <Link className="link" to="/verwaltung/rollen">
          Rollen und Rechte
        </Link>{' '}
        fest.{noRole > 0 && ` ${noRole === 1 ? '1 Nutzer wartet' : `${noRole} Nutzer warten`} auf eine Rolle.`}
      </Note>
      <Panel title={`${q.data!.length} Nutzer`}>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Rolle</th>
                <th>Kürzel</th>
                <th>Aktiv</th>
                <th>Tagesmail</th>
                <th>Letzter Login</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.map((u) => (
                <tr key={u.id} className={u.is_active ? '' : 'row-off'}>
                  <td>
                    {u.display_name}
                    <span className="sm">
                      {u.email}
                      {u.is_local ? ' · lokales Konto' : ''}
                    </span>
                  </td>
                  <td>
                    <Dropdown aria-invalid={!u.roles.length} value={u.roles[0] ?? ''} onChange={(v) => {
                        const role = v as RoleKey | '';
                        if (role === '') setPending({ kind: 'role', user: u, role });
                        else setRole.mutate({ user: u, role });
                      }} aria-label={`Rolle für ${u.display_name}`} disabled={setRole.isPending} size="sm" placeholder="keine Rolle" options={ROLE_KEYS.map((r) => ({ value: String(r), label: ROLE_NAMES[r] }))} />
                  </td>
                  <td>
                    <input className="tbl-input tbl-input-xs" defaultValue={u.initials ?? ''} maxLength={5} onBlur={(e) => e.target.value !== (u.initials ?? '') && patch.mutate({ user: u, initials: e.target.value.toUpperCase() || null })} aria-label={`Kürzel für ${u.display_name}`} />
                  </td>
                  <td>
                    <input type="checkbox" checked={u.is_active} onChange={(e) => (e.target.checked ? patch.mutate({ user: u, is_active: true }) : setPending({ kind: 'deactivate', user: u }))} aria-label={`${u.display_name} aktiv`} disabled={self(u)} title={self(u) ? 'Du kannst dich nicht selbst deaktivieren' : undefined} />
                  </td>
                  <td>
                    <input type="checkbox" checked={u.daily_digest} onChange={(e) => patch.mutate({ user: u, daily_digest: e.target.checked })} aria-label={`Tagesmail für ${u.display_name}`} />
                  </td>
                  <td className="mut small">{u.last_login_at ? fmtDateTime(u.last_login_at) : 'noch nie'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {pending?.kind === 'role' && (
        <ConfirmDialog
          title="Rolle entfernen?"
          text={`${pending.user.display_name} verliert damit jeden Zugriff auf die Projektdatenbank, bis wieder eine Rolle vergeben ist.`}
          confirmLabel="Rolle entfernen"
          danger
          busy={setRole.isPending}
          onConfirm={() => setRole.mutate({ user: pending.user, role: '' })}
          onClose={() => setPending(null)}
        />
      )}
      {pending?.kind === 'deactivate' && (
        <ConfirmDialog
          title={`${pending.user.display_name} deaktivieren?`}
          text="Die Person kann sich nicht mehr anmelden. Ihre Einträge und Zuständigkeiten bleiben erhalten."
          confirmLabel="Deaktivieren"
          danger
          busy={patch.isPending}
          onConfirm={() => patch.mutate({ user: pending.user, is_active: false })}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}
